import { z } from 'zod'

/**
 * batch-46 relaties-kvk (FO-395) — gedeelde types voor de periodieke
 * KVK-mutatiescan op relaties.
 *
 * Eén bron voor: de KVK-registratiestatus (badge), het mutatie-veld + de
 * voorstel-status, de DTO's voor de beoordelingslijst + aanvul-werkvoorraad,
 * en de request-schema's die de API én het form delen (CLAUDE.md — één
 * request-schema per mutatie-endpoint zodat het form nooit iets stuurt dat de
 * API weigert).
 */

// ─── Enums (const-arrays → union-types, spiegel de Prisma-enums) ─────────────

/** Registratiestatus zoals KVK die meldt (drijft de badge + facturatie-waarschuwing). */
export const KVK_REGISTRATIE_STATUS = [
  'ACTIEF',
  'OPGEHEVEN',
  'FAILLISSEMENT',
  'SURSEANCE',
] as const
export type KvkRegistratieStatus = (typeof KVK_REGISTRATIE_STATUS)[number]

/** Het veld waarop een KVK-afwijking is gedetecteerd. */
export const KVK_MUTATIE_VELD = ['NAAM', 'HANDELSNAAM', 'ADRES', 'REGISTRATIESTATUS'] as const
export type KvkMutatieVeld = (typeof KVK_MUTATIE_VELD)[number]

/** Status van een voorstel; nooit auto-apply (OPEN blijft tot een mens beslist). */
export const KVK_MUTATIE_STATUS = ['OPEN', 'OVERGENOMEN', 'GENEGEERD'] as const
export type KvkMutatieStatus = (typeof KVK_MUTATIE_STATUS)[number]

export const KVK_MUTATIE_VELD_LABELS: Record<KvkMutatieVeld, string> = {
  NAAM: 'Statutaire naam',
  HANDELSNAAM: 'Handelsnaam',
  ADRES: 'Vestigingsadres',
  REGISTRATIESTATUS: 'Registratiestatus',
}

export const KVK_REGISTRATIE_STATUS_LABELS: Record<KvkRegistratieStatus, string> = {
  ACTIEF: 'Actief',
  OPGEHEVEN: 'Opgeheven',
  FAILLISSEMENT: 'Faillissement',
  SURSEANCE: 'Surseance van betaling',
}

/**
 * De statussen die een acuut debiteurenrisico markeren: hoge-prio ACTIE +
 * blijvende badge + niet-blokkerende facturatie-waarschuwing.
 */
export function isKvkRisicoStatus(status: KvkRegistratieStatus | null | undefined): boolean {
  return status === 'OPGEHEVEN' || status === 'FAILLISSEMENT' || status === 'SURSEANCE'
}

/** Blijvende badge-tekst op de relatiekaart/-lijst. `null` = geen badge (actief/onbekend). */
export function kvkStatusBadgeLabel(status: KvkRegistratieStatus | null | undefined): string | null {
  switch (status) {
    case 'FAILLISSEMENT':
      return 'Faillissement (KVK)'
    case 'SURSEANCE':
      return 'Surseance (KVK)'
    case 'OPGEHEVEN':
      return 'Opgeheven (KVK)'
    default:
      return null
  }
}

/**
 * Niet-blokkerende waarschuwing in de facturatie-flow. `null` = geen
 * waarschuwing. Factureren blijft mogelijk; blokkeren is een menselijke
 * beslissing (spec §3.4 + aanname A3).
 */
export function kvkFacturatieWaarschuwing(
  status: KvkRegistratieStatus | null | undefined,
): string | null {
  switch (status) {
    case 'FAILLISSEMENT':
      return 'KVK meldt faillissement - controleer vóór verzending'
    case 'SURSEANCE':
      return 'KVK meldt surseance van betaling - controleer vóór verzending'
    case 'OPGEHEVEN':
      return 'KVK meldt dat deze relatie is opgeheven - controleer vóór verzending'
    default:
      return null
  }
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén locatie-optie bij een ambigue adres-overname (meerdere locaties). */
export interface KvkLocatieOptieDTO {
  id: string
  name: string
  isPrimair: boolean
  huidigAdres: string | null
}

/** Eén voorstel (oud vs. nieuw) op de beoordelingslijst / relatie-banner. */
export interface KvkMutatieVoorstelDTO {
  id: string
  clientId: string
  veld: KvkMutatieVeld
  veldLabel: string
  oudeWaarde: string | null
  nieuweWaarde: string
  status: KvkMutatieStatus
  gedetecteerdOp: string
  /** Alleen bij veld=ADRES en meerdere locaties: dan is een locatie-keuze verplicht. */
  ambigueLocatie: boolean
  /** Locatie-opties bij een ambigue adres-overname (leeg als niet ambigu). */
  locaties: KvkLocatieOptieDTO[]
}

/** De voorstellen van één relatie, gegroepeerd (lijst + banner). */
export interface KvkMutatieClientGroepDTO {
  clientId: string
  clientNumber: string
  clientNaam: string
  kvkNumber: string | null
  registratieStatus: KvkRegistratieStatus | null
  voorstellen: KvkMutatieVoorstelDTO[]
}

/** Een relatie zonder KVK-nummer in de aanvul-werkvoorraad. */
export interface KvkAanvulItemDTO {
  clientId: string
  clientNumber: string
  naam: string
  plaats: string | null
  type: string
}

/** Eén treffer van de KVK-naam-zoekhulp. */
export interface KvkZoekResultaatDTO {
  kvkNummer: string
  naam: string
  plaats: string | null
}

// ─── Request-schema's (gedeeld door API + form) ──────────────────────────────

/** Overnemen: optionele locatie-keuze bij een ambigue adres-overname. */
export const overnemenVoorstelSchema = z.object({
  locatieId: z.string().min(1).nullable().optional(),
})
export type OvernemenVoorstelInput = z.infer<typeof overnemenVoorstelSchema>

/** Negeren: optionele reden. */
export const negeerVoorstelSchema = z.object({
  reden: z.string().trim().max(500).nullable().optional(),
})
export type NegeerVoorstelInput = z.infer<typeof negeerVoorstelSchema>

/** KVK-nummer vastleggen vanuit de zoekhulp (8 cijfers). */
export const setKvkNummerSchema = z.object({
  kvkNumber: z.string().regex(/^\d{8}$/, 'Een KVK-nummer bestaat uit 8 cijfers'),
})
export type SetKvkNummerInput = z.infer<typeof setKvkNummerSchema>

/** "Geen KVK van toepassing" / uitsluiten (of terugzetten) van de scan. */
export const kvkUitsluitenSchema = z.object({
  uitgesloten: z.boolean(),
  reden: z.string().trim().max(500).nullable().optional(),
})
export type KvkUitsluitenInput = z.infer<typeof kvkUitsluitenSchema>

/** Naam-zoekhulp query. */
export const kvkZoekenQuerySchema = z.object({
  naam: z.string().trim().min(2).max(120),
})
export type KvkZoekenQueryInput = z.infer<typeof kvkZoekenQuerySchema>
